import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography } from '@mui/material';
import SensorMonitoring from './images/vpsi-icons_sensor.png';
import AirQuality from './images/vpsi-icons_air-quality.png';
import Engineering from './images/vpsi-icons_engineering.png';
import Analysis from './images/vpsi-icons_analysis.png';
import USA from './images/vpsi-icons_us-flag.png';

const StyledBox = styled(Box)(({ theme, noBorder }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  margin: '0 auto',
  justifyContent: 'center',
  padding: '40px 0',
  borderBottom: noBorder ? 'none' : `3px solid ${theme.palette.error.main}`,
  width: '75%',
}));

const StyledText = styled(Typography)({
  fontFamily: 'ZalandoSans',
  fontWeight: '600',
  fontSize: '25px !important',
  lineHeight: '1.2',
  marginTop: 0,
  width: '90%',

  [`@media screen and (max-width: 814px)`]: {
    width: '80%',
  },
});

const SolutionItem = [
  {
    title: 'Real-time Video & Sensor Monitoring',
    description:
      'Capture second-by-second data for both area and personal sampling, providing precision-driven insights.',
    icon: SensorMonitoring,
  },
  {
    title: 'Know the Air You Breathe',
    description: 'Detect airborne pollutants, assess air quality, and take timely action.',
    icon: AirQuality,
  },
  {
    title: 'Engineering Controls That Work',
    description: 'Implement best practices to create safer, healthier environments for your team.',
    icon: Engineering,
  },
  {
    title: 'Comprehensive Air Quality Analysis',
    description:
      'Monitor particulate matter, CO₂, VOCs, temperature, humidity, and location, all through a compact, portable system.',
    icon: Analysis,
  },
  {
    title: 'Built in the USA',
    description: 'Unmatched quality and reliability, crafted with American expertise.',
    icon: USA,
  },
];

function SolutionCard({ title, description, icon }) {
  return (
    <Box sx={{ width: { md: '15vw', xs: '80vw' }, maxWidth: { md: '200px', xs: 'unset' } }}>
      <Box
        sx={{
          bgcolor: 'error.main',
          display: 'flex',
          justifyContent: 'center',
          mb: 4,
          borderRadius: '50%',
          width: { md: '165px', xs: '50vw' },
          height: { md: '165px', xs: '50vw' },
          mx: 'auto',
        }}
      >
        <img
          src={icon}
          alt={`${title} icon`}
          style={{
            width: { md: '115px', xs: '90%' },
            maxWidth: '70%',
            height: 'auto',
            objectFit: 'contain',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          mb: 1,
          textAlign: 'center',
          fontFamily: 'ZalandoSans',
          fontWeight: '800',
          fontSize: { md: 'inherit', xs: '30px' },
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          fontFamily: 'ZalandoSans',
          fontWeight: '600',
          mb: { md: 0, xs: 4 },
          fontSize: { md: 'inherit', xs: '20px' },
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

export default function SolutionsSection() {
  return (
    <Box id="solutions" sx={{ p: '0!important' }}>
      <Box sx={{ bgcolor: 'action.selected' }}>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', pt: 6, pb: 3, px: { lg: '0', xs: '32px' } }}>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'ZalandoSans', fontWeight: '800' }}>
            WHY CHOOSE VPSi?
          </Typography>
          <Typography
            variant="h4"
            sx={{ mt: 3, fontFamily: 'ZalandoSans', fontWeight: '600', fontSize: '23px !important' }}
          >
            After decades in occupational safety and health, we saw a persistent gap: no economical, reliable tool
            existed to track CO₂, VOCs, temperature, humidity, formaldehyde, and airborne particulates in the critical
            2.5–10 µm range. That’s why we built our first product, the Air Quality Sensor 2.0—everything you need in
            one small package.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 5,
              flexDirection: { md: 'row', xs: 'column' },
              alignItems: { md: 'unset', xs: 'center' },
            }}
          >
            {SolutionItem.map((item, index) => (
              <SolutionCard key={index} title={item.title} description={item.description} icon={item.icon} />
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'common.black' }}>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', py: 4 }}>
          <Typography
            variant="h2"
            sx={{ textAlign: 'center', fontFamily: 'ZalandoSans', fontWeight: '800', color: 'common.white' }}
          >
            Trusted by Industry Leaders
          </Typography>
        </Box>
      </Box>
      <Box sx={{ maxWidth: '1200px', margin: '0 auto', pb: 6 }}>
        <StyledBox>
          <CheckCircleIcon color="error" sx={{ fontSize: '50px' }} />
          <StyledText variant="h4">Over 35 years of experience in workplace safety</StyledText>
        </StyledBox>
        <StyledBox>
          <CheckCircleIcon color="error" sx={{ fontSize: '50px' }} />
          <StyledText variant="h4">Veteran-owned and operated, based in Grand Rapids, Michigan</StyledText>
        </StyledBox>
        <StyledBox>
          <CheckCircleIcon color="error" sx={{ fontSize: '50px' }} />
          <StyledText variant="h4">
            An SBA-registered small business supporting government, industry, and academic partners
          </StyledText>
        </StyledBox>
        <StyledBox noBorder>
          <CheckCircleIcon color="error" sx={{ fontSize: '50px' }} />
          <StyledText variant="h4">
            Trusted across government, non-government, and global academic institutions that rely on proven, scalable
            solutions to elevate performance and outcomes
          </StyledText>
        </StyledBox>
      </Box>
    </Box>
  );
}
